"use client";

import { useState, useEffect } from "react";
import { SellerAddressType, SellerDto } from "../../../../types/Seller.types";
import { MapPin } from "lucide-react";
import Image from "next/image";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@ui/components/dialog";

export const SellerHeaderDetails = ({ seller }: { seller: SellerDto }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const { addresses } = seller;

  useEffect(() => {
    const isMobileDevice =
      typeof window !== "undefined" &&
      /Mobi|Android/i.test(navigator.userAgent);
    setIsMobile(isMobileDevice);
  }, []);

  const location = addresses?.find(
    (address) => address.addressType === SellerAddressType.STOREFRONT
  );

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const wazeLink = location
    ? isMobile
      ? `waze://?q=${encodeURIComponent(
          `${location.addressLine}, ${location.number || ""} ${location.city}, ${location.state}`
        )}&navigate=yes`
      : `https://www.waze.com/ul?q=${encodeURIComponent(
          `${location.addressLine}, ${location.number || ""} ${location.city}, ${location.state}`
        )}`
    : null;

  const googleMapsLink = location
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
        `${location.addressLine}, ${location.number || ""} ${location.city}, ${location.state}`
      )}`
    : null;

  return (
    <div className="relative border-b pb-2 border-gray-200 flex flex-col !justify-center items-center w-full">
      <div className="mt-6 mb-5 rounded-full overflow-hidden border-[1px] w-fit">
        <Image
          alt={seller.name || "Seller Avatar"}
          src={
            seller.avatarUrl
              ? `https://cdn.click.autos/${seller.avatarUrl}`
              : `https://api.dicebear.com/9.x/initials/png?seed=${seller.name}&fontFamily=Tahoma&backgroundColor=fbbf24`
          }
          width={233}
          height={233}
          className="align-middle inline-block max-w-full h-auto rounded-lg hover:scale-110 transition-all ease-linear cursor-pointer"
          priority
        />
      </div>
      <div className="flex flex-col justify-center items-center w-full">
        <div>
          <h1 className="mb-3 font-medium text-3xl text-center">
            {seller.name}
          </h1>
          <ul className="mb-5 flex flex-col gap-2">
            {location ? (
              <li
                className="text-sm relative mr-8 flex items-center cursor-pointer"
                onClick={openModal}
              >
                <MapPin className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                <span className="ml-2">
                  {[
                    location.addressLine,
                    `Nº ${location.number}`,
                    location.city,
                    location.state,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </span>
              </li>
            ) : (
              <li className="text-sm relative mr-8 flex items-center">
                <MapPin className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                <span className="ml-2">Endereço não disponível</span>
              </li>
            )}
          </ul>
        </div>
      </div>
      {location && (
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Abrir endereço com:</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-4">
              {wazeLink && (
                <a
                  href={wazeLink}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="bg-blue-500 text-white py-2 px-4 rounded-lg text-center"
                  onClick={closeModal}
                >
                  Waze
                </a>
              )}
              {googleMapsLink && (
                <a
                  href={googleMapsLink}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="bg-green-500 text-white py-2 px-4 rounded-lg text-center"
                  onClick={closeModal}
                >
                  Google Maps
                </a>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
